import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { IconButton, Stack, useTheme } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import Button from "components/basic/button";
import Typography from "components/basic/typography";
import { useSnack } from "hooks/useSnack";
import { convertHeicToJpeg, isHeic } from "utils/heic";
import {
  ACCEPTED_FILE_TYPES,
  MAX_ATTACHMENTS_PER_MESSAGE,
  MAX_ATTACHMENT_BYTES,
} from "pages/dashboard/pages/message/constants";

interface AttachmentPickerProps {
  files: File[];
  disabled: boolean;
  onChange: (files: File[]) => void;
}

const isAllowedType = (file: File) =>
  file.type === "application/pdf" ||
  file.type.startsWith("image/") ||
  isHeic(file);

/** Same limits as message attachments: images and PDFs, 10MB, 10 files. */
const AttachmentPicker: React.FC<AttachmentPickerProps> = ({
  files,
  disabled,
  onChange,
}) => {
  const theme = useTheme();
  const showSnack = useSnack();
  const { t } = useTranslation("support");
  const [converting, setConverting] = useState(false);

  const handleAdd = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = "";

    const accepted: File[] = [];
    for (const file of picked) {
      if (!isAllowedType(file)) {
        showSnack(t("snack.type_not_allowed", { file: file.name }), "error");
      } else if (file.size > MAX_ATTACHMENT_BYTES) {
        showSnack(t("snack.too_large", { file: file.name }), "error");
      } else {
        accepted.push(file);
      }
    }

    if (accepted.some(isHeic)) {
      setConverting(true);
      try {
        for (let i = 0; i < accepted.length; i++) {
          accepted[i] = await convertHeicToJpeg(accepted[i]);
        }
      } finally {
        setConverting(false);
      }
    }

    const next = [...files, ...accepted];
    if (next.length > MAX_ATTACHMENTS_PER_MESSAGE) {
      showSnack(
        t("snack.too_many", { max: MAX_ATTACHMENTS_PER_MESSAGE }),
        "error",
      );
    }
    onChange(next.slice(0, MAX_ATTACHMENTS_PER_MESSAGE));
  };

  return (
    <Stack spacing={1} alignItems="flex-start">
      <Button
        size="small"
        component="label"
        disabled={disabled}
        loading={converting}
        startAdornment={<AttachFileIcon fontSize="small" />}
      >
        {t("dialog.attach")}
        <input
          hidden
          type="file"
          multiple
          accept={ACCEPTED_FILE_TYPES}
          onChange={handleAdd}
        />
      </Button>

      {files.map((file, idx) => (
        <Stack
          key={`${file.name}-${idx}`}
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{
            backgroundColor: theme.palette.action.hover,
            borderRadius: 1,
            pl: 1,
            maxWidth: "100%",
          }}
        >
          <Typography variant="body2" noWrap>
            {file.name}
          </Typography>
          <IconButton
            size="small"
            onClick={() => onChange(files.filter((_, i) => i !== idx))}
            aria-label={t("dialog.remove", { file: file.name })}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Stack>
      ))}
    </Stack>
  );
};

export default AttachmentPicker;
